'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/**
 * Composición "partida": la foto ocupa una mitad y el contenido la otra, sobre
 * el fondo del tema. NO es de ningún tema en particular — solo lee tokens
 * (--ms-*), así que cualquiera puede elegirla sin heredar el color de firma de
 * otro tema.
 *
 * Su ventaja real no es estética: el texto NUNCA va encima de la foto, así que
 * se lee perfecto aunque el merchant suba una imagen clara, cargada o con la
 * comida justo detrás del titular — el caso que obliga a subir el velo en las
 * composiciones a sangre. Sin media se degrada a una columna centrada.
 */
export function HeroSplit({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section
      // flex en móvil (no solo grid en lg): sin contenedor flex/grid el
      // `order-first` de la media no aplica y la foto queda DEBAJO del texto.
      className="relative flex w-full flex-col overflow-hidden lg:grid lg:grid-cols-2 lg:items-stretch"
      style={{ background: 'var(--ms-bg)' }}
    >
      {/* Contenido — sobre el fondo del tema, nunca sobre la foto */}
      <div
        className="relative z-10 flex flex-col items-center justify-center px-6 py-16 text-center lg:items-start lg:px-14 lg:text-left xl:px-20"
        style={{ minHeight: hasMedia ? 'min(560px, 72vh)' : 'min(620px, 80vh)' }}
      >
        {city && (
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.32em] pl-[0.32em]"
            style={{ color: 'var(--ms-accent)' }}
          >
            {city}
          </span>
        )}

        <h1
          className="mt-5 leading-[1.04] text-[clamp(38px,7vw,68px)]"
          style={{ fontFamily: 'var(--ms-font-display)', color: 'var(--ms-text)' }}
        >
          {businessName}
        </h1>

        {tagline && (
          <p
            className="mt-4 max-w-md text-[16px] sm:text-[17px] leading-relaxed"
            style={{ color: 'var(--ms-text-soft)' }}
          >
            {tagline}
          </p>
        )}

        <div
          className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12.5px] tracking-wide lg:justify-start"
          style={{ color: 'var(--ms-text-muted)' }}
        >
          {statusLabel && (
            <span className="inline-flex items-center gap-2">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? '#5fcf80'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] transition hover:brightness-110 active:scale-[0.98]"
            style={{
              background: 'var(--ms-accent)',
              // La tinta del botón es el fondo del tema: en los oscuros el acento
              // es claro y necesita texto oscuro encima, y al revés.
              color: 'var(--ms-bg)',
              borderRadius: 'var(--ms-radius)',
              boxShadow: 'var(--ms-card-shadow)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-7 py-4 text-[12.5px] font-semibold uppercase tracking-[0.14em] transition"
              style={{
                color: 'var(--ms-text)',
                borderRadius: 'var(--ms-radius)',
                border: '1px solid color-mix(in srgb, var(--ms-accent) 45%, transparent)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>

      {/* Media — mitad derecha en desktop, banda superior en móvil */}
      {hasMedia && (
        <div className="relative order-first h-[46vh] min-h-[260px] w-full lg:order-none lg:h-auto lg:min-h-full">
          {heroVideoUrl ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              className="absolute inset-0 h-full w-full object-cover"
              src={heroVideoUrl}
            />
          ) : (
            <Image
              src={heroImageUrl as string}
              alt={businessName}
              fill
              priority
              sizes="(max-width: 1024px) 100vw, 50vw"
              className="object-cover"
            />
          )}
          {/* Difuminado hacia el contenido: une las dos mitades en vez de
              cortarlas con una línea dura. Vertical en móvil, horizontal en
              desktop (donde la foto queda al lado, no arriba). */}
          <div
            className="absolute inset-0 lg:hidden"
            style={{ background: 'linear-gradient(to bottom, transparent 55%, var(--ms-bg))' }}
          />
          <div
            className="absolute inset-0 hidden lg:block"
            style={{ background: 'linear-gradient(to right, var(--ms-bg), transparent 22%)' }}
          />
        </div>
      )}
    </section>
  )
}
